/**
 * @file Barre sociale du hub en ligne : Discussion (messages du salon, joueurs presents, bloquer / signaler / ajouter en ami), Emotes (roue de 6),
 * accès à l'écran En ligne. Visible uniquement dans le hub ; les boutons de discussion et d'emotes n'existent que si un serveur est configure.
 */
import { Component } from '../core/Component';
import { clear, h, setText, toggleClass } from '../core/dom';
import { notify, openScreen } from '../core/feedback';
import { uiIcon } from '../core/uiIcons';
import type { UiIconId } from '../core/uiIcons';
import type { UiChatLine, UiContext, UiHubPeer } from '../UiContext';

export const EMOTE_EMOJI: Record<string, string> = { wave: '👋', heart: '💖', laugh: '😹', dance: '💃', sleep: '💤', angry: '💢' };

export class HubSocial extends Component {
  private chatBtn!: HTMLElement;
  private chatBadge!: HTMLElement;
  private onlineBadge!: HTMLElement;
  private emotePop!: HTMLElement;
  private panel!: HTMLElement;
  private list!: HTMLElement;
  private players!: HTMLElement;
  private input!: HTMLInputElement;
  private statusLine!: HTMLElement;
  private menu: HTMLElement | null = null;
  private open = false;
  private showPlayers = false;
  private dirty = true;
  private shownLines = -1;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'hub-social');
  }

  protected build(): void {
    const { i18n, hub, online } = this.ctx;
    const t = i18n;
    const btn = (cls: string, icon: UiIconId, label: string, onclick: () => void): { root: HTMLElement; badge: HTMLElement } => {
      const badge = h('span', { class: 'badge is-hidden' }, '');
      const root = h('button', { class: `hub-btn ${cls}`, type: 'button', 'data-ui-block': true, onclick }, h('span', { class: 'hub-btn-icon' }, uiIcon(icon)), h('span', { class: 'hub-btn-label' }, label), badge);
      return { root, badge };
    };
    const row = h('div', { class: 'hub-social-row' });
    if (hub) {
      const chat = btn('hub-chat', 'chat', t.t('hub.chat'), () => this.toggleChat());
      this.chatBtn = chat.root;
      this.chatBadge = chat.badge;
      const emotes = btn('hub-emotes', 'emote', t.t('hub.emotes'), () => this.toggleEmotes());
      row.append(chat.root, emotes.root);
    }
    if (online) {
      const on = btn('hub-online', 'globe', t.t('menu.online'), () => openScreen('online'));
      this.onlineBadge = on.badge;
      row.append(on.root);
    }

    this.emotePop = h('div', { class: 'emote-pop is-hidden' });
    if (hub) {
      for (const id of hub.emotes) {
        this.emotePop.append(h('button', { class: 'emote-btn', type: 'button', title: t.tDyn(`hub.emote.${id}`), 'data-ui-block': true, onclick: () => (hub.sendEmote(id), this.toggleEmotes(false)) }, EMOTE_EMOJI[id] ?? '❔'));
      }
    }

    this.list = h('div', { class: 'chat-list' });
    this.players = h('div', { class: 'chat-players is-hidden' });
    this.statusLine = h('div', { class: 'hint chat-status' });
    this.input = h('input', { class: 'text-input', type: 'text', maxlength: 120, autocomplete: 'off', placeholder: t.t('hub.chat.placeholder'), enterkeyhint: 'send' });
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.send();
    });
    const send = h('button', { class: 'btn small primary', type: 'button', 'data-ui-block': true, onclick: () => this.send() }, t.t('hub.chat.send'));
    const playersBtn = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => this.togglePlayers() }, uiIcon('people'), t.t('hub.players'));
    const close = h('button', { class: 'btn small', type: 'button', 'aria-label': t.t('common.close'), 'data-ui-block': true, onclick: () => this.toggleChat(false) }, '✕');
    this.panel = h(
      'div',
      { class: 'chat-panel panel is-hidden', 'data-ui-block': true },
      h('div', { class: 'chat-head' }, h('b', {}, t.t('hub.chat')), this.statusLine, playersBtn, close),
      this.list,
      this.players,
      h('div', { class: 'chat-input' }, this.input, send),
    );
    this.el.append(this.panel, this.emotePop, row);

    if (hub) this.addDisposer(hub.onChange(() => (this.dirty = true)));
    if (online) this.addDisposer(online.onStatus(() => (this.dirty = true)));
    this.addDisposer(() => this.closeMenu());
    this.update(0);
  }

  private connected(): boolean {
    return this.ctx.hub?.link === 'connected';
  }

  private toggleEmotes(force?: boolean): void {
    const next = force ?? this.emotePop.classList.contains('is-hidden');
    if (next && !this.connected()) {
      notify(this.ctx.i18n.t('hub.chat.offline'), 'info');
      return;
    }
    toggleClass(this.emotePop, 'is-hidden', !next);
    if (next) this.toggleChat(false);
  }

  private toggleChat(force?: boolean): void {
    const next = force ?? !this.open;
    if (next && !this.ctx.hub) return;
    this.open = next;
    toggleClass(this.panel, 'is-hidden', !next);
    if (next) {
      this.toggleEmotes(false);
      if (this.ctx.hub) this.ctx.hub.unread = 0;
      this.dirty = true;
      this.shownLines = -1;
    } else {
      this.closeMenu();
      this.input.blur();
    }
    this.update(0);
  }

  private togglePlayers(): void {
    this.showPlayers = !this.showPlayers;
    this.dirty = true;
    this.update(0);
  }

  private send(): void {
    const hub = this.ctx.hub;
    if (!hub) return;
    if (!this.connected()) {
      notify(this.ctx.i18n.t('hub.chat.offline'), 'info');
      return;
    }
    const text = this.input.value.trim();
    if (!text) return;
    hub.sendChat(text);
    this.input.value = '';
  }

  // ------------------------------------------------------------------------------------------------ actions sur un joueur

  private closeMenu(): void {
    this.menu?.remove();
    this.menu = null;
  }

  /** Petite ligne d'actions sous un message / un joueur : ajouter en ami, bloquer, signaler. */
  private openMenu(anchor: HTMLElement, id: string, name: string): void {
    this.closeMenu();
    const { hub, online, i18n } = this.ctx;
    if (!hub || id === hub.selfId) return;
    const mk = (label: string, fn: () => void): HTMLElement =>
      h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => (this.closeMenu(), fn()) }, label);
    const menu = h('div', { class: 'chat-menu' });
    if (online) {
      menu.append(
        mk(i18n.t('hub.chat.addFriend'), () =>
          void online.addFriend(name).then((r) => notify(r.ok ? i18n.t(r.value === 'friend' ? 'online.friend.added' : 'online.friend.sent') : i18n.tDyn(`online.err.${r.error}`) === `online.err.${r.error}` ? i18n.t('online.err.generic') : i18n.tDyn(`online.err.${r.error}`), r.ok ? 'success' : 'warning')),
        ),
      );
    }
    menu.append(
      mk(i18n.t('hub.chat.block'), () => hub.block(id)),
      mk(i18n.t('hub.chat.report'), () => hub.report(id, 'chat')),
    );
    anchor.after(menu);
    this.menu = menu;
  }

  // ------------------------------------------------------------------------------------------------ rendu

  private renderLine(l: UiChatLine): HTMLElement {
    const body = l.text ? l.text : (EMOTE_EMOJI[l.emote ?? ''] ?? '');
    const el = h('div', { class: `chat-line${l.mine ? ' mine' : ''}` }, h('b', { class: 'who' }, l.fromName), h('span', { class: 'txt' }, body));
    if (!l.mine) el.addEventListener('click', () => this.openMenu(el, l.fromId, l.fromName));
    return el;
  }

  private renderPlayers(peers: readonly UiHubPeer[]): void {
    clear(this.players);
    if (peers.length === 0) this.players.append(h('p', { class: 'hint' }, this.ctx.i18n.t('online.empty')));
    for (const p of peers) {
      const row = h('div', { class: 'chat-player' }, h('span', { class: 'nm' }, p.nickname || '…'));
      row.addEventListener('click', () => this.openMenu(row, p.id, p.nickname));
      this.players.append(row);
    }
  }

  override update(_dt: number): void {
    const { hub, online, i18n } = this.ctx;
    if (online && this.onlineBadge) {
      setText(this.onlineBadge, String(online.badge));
      toggleClass(this.onlineBadge, 'is-hidden', online.badge <= 0);
    }
    if (!hub) return;
    const unread = hub.unread;
    setText(this.chatBadge, String(unread));
    toggleClass(this.chatBadge, 'is-hidden', unread <= 0 || this.open);
    if (!this.open) return;
    // le panneau est ouvert : les nouveaux messages sont lus tout de suite
    if (hub.unread !== 0) hub.unread = 0;
    if (!this.dirty && this.shownLines === hub.chat.length) return;
    this.dirty = false;
    this.shownLines = hub.chat.length;
    setText(this.statusLine, this.connected() ? `${i18n.t('hub.link.connected')} · ${hub.players.length + 1}` : i18n.t(hub.link === 'connecting' ? 'hub.link.connecting' : 'hub.link.solo'));
    toggleClass(this.list, 'is-hidden', this.showPlayers);
    toggleClass(this.players, 'is-hidden', !this.showPlayers);
    this.closeMenu();
    if (this.showPlayers) {
      this.renderPlayers(hub.players);
      return;
    }
    clear(this.list);
    if (!this.connected()) this.list.append(h('p', { class: 'hint' }, i18n.t('hub.chat.offline')));
    else if (hub.chat.length === 0) this.list.append(h('p', { class: 'hint' }, i18n.t('hub.chat.empty')));
    for (const l of hub.chat) this.list.append(this.renderLine(l));
    this.list.scrollTop = this.list.scrollHeight;
  }
}
